import {Modal} from '@/components/atoms'
import {SaveCancelFooter} from '@/components/molecules'
import {useBrandAndCategoryEditHook} from '@/containers/ProductReviewEditContainer/BrandAndCategory/BrandAndCategoryEditHook'
import BrandEdit from '@/containers/ProductReviewEditContainer/BrandAndCategory/BrandEdit/BrandEdit'
import CategoryEdit from '@/containers/ProductReviewEditContainer/BrandAndCategory/CategoryEdit/CategoryEdit'
import {normalizeSubCategories} from '@/helpers/shadcn/utils'
import {useFeatureFlagLanguages} from '@/hooks/useFeatureFlagLanguages'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {selectIsIndia} from '@/store/slices/countryCodeSlice'
import {MODAL_TYPE} from '@/utils/constants/globalStyle'
import {ROUTES} from '@/utils/constants/routesConstants'
import {useEffect, useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import './brandAndCategoryEdit.scss'

export const BrandAndCategoryEdit = ({formData, setFormData, handleSave}) => {
  const {brandFields, categoryFields, categoryListData, brandAndCategoryFormData} = useBrandAndCategoryEditHook({
    formData,
    setFormData,
  })
  const navigate = useNavigate()
  const isSavingRef = useRef(false)
  const dispatch = useAppDispatch()
  const [localFormData, setLocalFormData] = useState(() => extractLocalFormData(formData))
  const [shouldShowModal, setShouldShowModal] = useState(false)
  const isIndia = useSelector(selectIsIndia)

  const languages = useFeatureFlagLanguages()
  useEffect(() => {
    if (!isSavingRef.current) {
      setLocalFormData(extractLocalFormData(formData))
    }
  }, [formData])

  // const handleInputChange = (_data, field, value, lang = null) => {
  //   // Use functional state update to always work with the latest snapshot
  //   setLocalFormData(prevLocal => {
  //     const nextLocal = {
  //       ...prevLocal,
  //       [field]: lang ? {...(prevLocal[field] || {}), [lang]: value} : value,
  //     }

  //     // Build the complete payload for the parent component
  //     const nextFormData = {
  //       ...formData,
  //       brandInfo: {
  //         ...formData?.brandInfo,
  //         ...pickBrandFields(nextLocal),
  //       },
  //       category: {
  //         ...formData?.category,
  //         ...pickCategoryFields(nextLocal),
  //       },
  //     }

  //     setFormData(nextFormData)

  //     return nextLocal
  //   })
  // }
  const addEmptySubCategory = () => {
    setLocalFormData(prev => {
      const prevArray = Array.isArray(prev.subCategories) ? [...prev.subCategories] : []

      if (prevArray.length >= 10) return prev

      const newSubCategory = {
        code: '',
        values: {},
        url: '',
      }

      const nextArray = [...prevArray, newSubCategory]

      const nextLocal = {
        ...prev,
        subCategories: nextArray,
      }

      // sync with parent form
      const nextFormData = {
        ...formData,
        category: {
          ...formData?.category,
          ...pickCategoryFields(nextLocal),
        },
        brandInfo: {
          ...formData?.brandInfo,
          ...pickBrandFields(nextLocal),
        },
      }
      setFormData(nextFormData)
      return nextLocal
    })
  }
  const removeSubCategory = index => {
    setLocalFormData(prev => {
      const prevArray = Array.isArray(prev.subCategories) ? [...prev.subCategories] : []

      // Remove by index
      const nextArray = prevArray.filter((_, i) => i !== index)

      const nextLocal = {
        ...prev,
        subCategories: nextArray,
      }

      // Update parent form data
      const nextFormData = {
        ...formData,
        category: {
          ...formData?.category,
          ...pickCategoryFields(nextLocal),
        },
        brandInfo: {
          ...formData?.brandInfo,
          ...pickBrandFields(nextLocal),
        },
      }

      setFormData(nextFormData)
      return nextLocal
    })
  }

  const handleInputChange = (_data, field, value, lang = null, opts = {}) => {
    const {idx} = opts
    setLocalFormData(prev => {
      let nextLocal = {
        ...prev,
        [field]: lang ? {...(prev[field] || {}), [lang]: value} : value,
      }

      if (!lang && !idx) {
        nextLocal[field] = value
      }

      if (idx >= 0 && lang) {
        const prevArray = Array.isArray(prev.subCategories) ? [...prev.subCategories] : []

        const existingIndex = Number.isInteger(idx) ? idx : -1

        if (existingIndex !== -1 && prevArray[existingIndex]) {
          const existing = prevArray[existingIndex]

          const actualValue = typeof value === 'object' && value?.target ? value.target.value : value

          prevArray[existingIndex] = {
            ...existing,

            // update code only when field == 'subCategoryId'
            code: field === 'subCategoryId' ? actualValue : existing.code,

            // update values only when field == 'subCategories'
            values:
              field === 'subCategories'
                ? {
                    ...(existing.values || {}),
                    [lang]: actualValue,
                  }
                : existing.values,
          }
        } else {
          // Create new subcategory
          const actualValue = typeof value === 'object' && value?.target ? value.target.value : value

          prevArray.push({
            code: field === 'subCategoryId' ? actualValue : '',
            values: field === 'subCategories' && lang ? {[lang]: actualValue} : {},
            url: '',
          })
        }

        nextLocal.subCategories = prevArray
      }

      const nextFormData = {
        ...formData,
        brandInfo: {
          ...formData?.brandInfo,
          ...pickBrandFields(nextLocal),
        },
        category: {
          ...formData?.category,
          ...pickCategoryFields(nextLocal),
        },
      }

      setFormData(nextFormData)

      return nextLocal
    })
  }

  const handleSaveAction = async () => {
    try {
      isSavingRef.current = true

      const updatedFormData = {
        ...formData,
        brandInfo: {
          ...formData?.brandInfo,
          ...pickBrandFields(localFormData),
        },
        category: {
          ...formData?.category,
          ...pickCategoryFields(localFormData),
        },
      }
      await handleSave(updatedFormData)
      setFormData(updatedFormData)
    } catch (error) {
      dispatch(
        setToastNotifications({
          show: true,
          message: `Error saving Display Information: ${error}`,
          type: 'error',
        })
      )
    } finally {
      isSavingRef.current = false
    }
  }

  const getFormError = () => {
    if (isIndia) return true
    const subCategories = [...(localFormData?.subCategories || [])]
    if (!Array.isArray(subCategories)) return false

    const requiredKeys = languages?.map(lang => lang?.code)

    // Check for duplicate subcategory codes
    const codes = subCategories.map(item => item?.code?.trim()).filter(Boolean)
    const hasDuplicateCodes = codes.length !== new Set(codes).size
    if (hasDuplicateCodes) return false

    const subCategoriesValid = subCategories.every(item => {
      if (!item || typeof item?.code !== 'string' || item?.code?.trim().length === 0) return false

      const values = item?.values
      if (!values || typeof values !== 'object' || Array.isArray(values)) return false

      const hasAllRequiredKeys = requiredKeys?.every(key => Object.prototype.hasOwnProperty.call(values, key))
      if (!hasAllRequiredKeys) return false

      const allValuesNonEmpty = Object.keys(values).every(key => {
        const val = values[key]
        return typeof val === 'string' && val?.trim().length > 0
      })

      return allValuesNonEmpty
    })

    if (!subCategoriesValid) return false

    const displayCategory = localFormData?.displayCategory
    if (
      !displayCategory ||
      typeof displayCategory !== 'object' ||
      Array.isArray(displayCategory) ||
      !requiredKeys?.every(key => displayCategory[key]?.trim()?.length > 0)
    ) {
      return false
    }

    const displayCategoryId = localFormData?.displayCategoryId
    if (typeof displayCategoryId !== 'string' || displayCategoryId.trim().length === 0) {
      return false
    }

    return true
  }

  // Helper to get duplicate subcategory code errors for display
  const getDuplicateSubCategoryErrors = () => {
    const subCategories = localFormData?.subCategories || []
    if (!Array.isArray(subCategories)) return {}

    const codeCount = {}
    subCategories?.forEach(item => {
      const code = item?.code?.trim()
      if (code) {
        codeCount[code] = (codeCount[code] || 0) + 1
      }
    })

    const errors = {}
    subCategories?.forEach((item, idx) => {
      const code = item?.code?.trim()
      if (code && codeCount[code] > 1) {
        errors[idx] = 'SubCategory with same ID is already added for this product'
      }
    })

    return errors
  }

  const subCategoryErrors = getDuplicateSubCategoryErrors()

  return (
    <div className="brand-category-edit">
      <BrandEdit
        brandAndCategoryFormData={brandAndCategoryFormData}
        handleFormChange={handleInputChange}
        brandFields={brandFields}
        languages={languages}
        localFormData={localFormData}
      />
      <CategoryEdit
        brandAndCategoryFormData={brandAndCategoryFormData}
        handleFormChange={handleInputChange}
        categoryListData={categoryListData}
        categoryFields={categoryFields}
        localFormData={localFormData}
        languages={languages}
        addEmptySubCategory={addEmptySubCategory}
        removeSubCategory={removeSubCategory}
        subCategoryErrors={subCategoryErrors}
        isMandatoryForIndia={isIndia}
      />
      {shouldShowModal && (
        <Modal
          type={MODAL_TYPE.SMALL}
          showModal={shouldShowModal}
          onCloseModal={() => setShouldShowModal(false)}
          title="Are you sure you want to cancel?"
          text={'The changes will not be saved.'}
          showPrimaryButton={true}
          primaryButtonText="Yes"
          onClickPrimaryButton={() => {
            navigate(`${ROUTES.PRODUCT_REVIEW.replace(':id', formData?.baseData?.productId)}`, {
              state: {selectedTabNumb: 3},
            })
          }}
          showSecondaryButton={true}
          secondaryButtonText="No"
          onClickSecondaryButton={() => setShouldShowModal(false)}
          footerProps={{
            size: 'medium',
            type: 'primary',
          }}
          isDark={true}
        />
      )}
      <SaveCancelFooter
        handleCancel={
          () => setShouldShowModal(value => !value) // Replace :id with the actual productId
        }
        nextAction={handleSaveAction}
        isReview={true}
        hideSaveDraft={true}
        FormError={!getFormError()}
      />
    </div>
  )
}

// 🔧 Extract fields for better reuse
const extractLocalFormData = formData => ({
  brandId: formData?.brandInfo?.brandId || '',
  displayBrandId: formData?.brandInfo?.displayBrandId || '',
  brandGroupName: formData?.brandInfo?.brandGroupName || '',
  subBrandId: formData?.brandInfo?.subBrandId || '',
  brand: formData?.brandInfo?.brand || {},
  displayBrand: formData?.brandInfo?.displayBrand || {},
  subBrand: formData?.brandInfo?.subBrand || {},
  categoryType: formData?.category?.categoryType || '',
  categories: formData?.category?.categories || [],
  erpCategory: formData?.category?.erpCategory || '',
  erpCategoryId: formData?.category?.erpCategoryId || '',
  displayCategoryId: formData?.category?.displayCategoryId || '',
  subCategoryId: formData?.category?.subCategoryId || '',
  displayCategory: formData?.category?.displayCategory || {},
  subCategories: Array.isArray(formData?.category?.subCategories)
    ? formData?.category?.subCategories
    : normalizeSubCategories({
        values: {...formData?.category?.subCategories},
        subCategoryId: formData?.category?.subCategoryId,
      }),
  subCategoryFlag: formData?.category?.subCategoryFlag || false,
})

const pickBrandFields = data => ({
  brandId: data.brandId,
  displayBrandId: data.displayBrandId,
  brandGroupName: data.brandGroupName,
  subBrandId: data.subBrandId,
  brand: data.brand,
  displayBrand: data.displayBrand,
  subBrand: data.subBrand,
})

const pickCategoryFields = data => {
  // Transform the categories to match the expected API format
  const transformedCategories = data?.categories?.map(category => ({
    categoryId: category.id || category.categoryId,
    categoryName: category.displayText || category.categoryName,
    categoryType: data?.categoryType || category.categoryType || '',
  }))

  return {
    erpCategory: data.erpCategory,
    erpCategoryId: data.erpCategoryId,
    displayCategoryId: data.displayCategoryId,
    displayCategory: data.displayCategory,
    subCategories: data.subCategories,
    categories: transformedCategories,
    categoryType: data?.categoryType || '',
    subCategoryFlag: data?.subCategoryFlag,
  }
}
